import { useState, useEffect, useCallback } from 'react'
import { useIdentity, useEngineManager } from '../../hooks/useWorkspace'
import { buildConfigWorkspaceSecret } from '../../engine/mcp/crypto'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import { encodeWorkspaceSecretV2 } from '@secureblob/sdk/workspaceSecretV2'
import type { McpConnectionEntry } from '../../engine/mcp/types'
import './McpAuthorizePage.css'

type AuthState =
    | 'loading'
    | 'session-valid'
    | 'vault-locked'
    | 'unlocking'
    | 'approved'
    | 'redirecting'
    | 'error'

interface SessionInfo {
    configId: string
    redirectDomain: string
    expiresAt: string
}

function isSessionInfo(value: unknown): value is SessionInfo {
    if (!value || typeof value !== 'object') return false
    const candidate = value as Partial<SessionInfo>
    return typeof candidate.configId === 'string'
        && typeof candidate.redirectDomain === 'string'
        && typeof candidate.expiresAt === 'string'
}

function hasRedirectUrl(value: unknown): value is { redirectUrl: string } {
    if (!value || typeof value !== 'object') return false
    const candidate = value as { redirectUrl?: unknown }
    return typeof candidate.redirectUrl === 'string' && candidate.redirectUrl.length > 0
}

async function readJsonBody(res: { json: () => Promise<unknown> }, invalidMessage: string) {
    try {
        return await res.json()
    } catch {
        throw new Error(invalidMessage)
    }
}

// Use relative paths to ensure consistency with the current origin and protocol.
const API_BASE = '';

/* ─── SVG Icons ─── */
const ShieldIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="url(#shield-grad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <defs>
            <linearGradient id="shield-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#818cf8" />
                <stop offset="100%" stopColor="#a78bfa" />
            </linearGradient>
        </defs>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
    </svg>
)

const KeyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7.5" cy="15.5" r="5.5" />
        <path d="m21 2-9.6 9.6" />
        <path d="m15.5 7.5 3 3L22 7l-3-3" />
    </svg>
)

const CheckCircleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="m9 12 2 2 4-4" />
    </svg>
)

const AlertIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
    </svg>
)

const ClockIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
)

const FolderIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    </svg>
)

export default function McpAuthorizePage() {
    const { hasIdentity, unlockWithWebAuthn, vaultExists } = useIdentity()
    const engine = useEngineManager()

    const [state, setState] = useState<AuthState>('loading')
    const [error, setError] = useState<string | null>(null)
    const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null)
    const [connection, setConnection] = useState<McpConnectionEntry | null>(null)

    // Parse URL params
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session') ?? ''
    const configId = params.get('configId') ?? ''

    // Fetch session info from server
    useEffect(() => {
        if (!sessionId || !configId) {
            setState('error')
            setError('Missing session or configId in URL')
            return
        }

        const sessionInfoUrl = `${API_BASE}/mcp/oauth/${configId}/session-info?session=${sessionId}`
        fetch(sessionInfoUrl)
            .then(async res => {
                recordBrowserHttpErrorResponse(res, { url: sessionInfoUrl, method: 'GET' })
                if (!res.ok) {
                    const body = await res.json().catch(() => ({}))
                    throw new Error(body.error ?? `Session error (${res.status})`)
                }
                const body = await readJsonBody(
                    res,
                    'Server returned an invalid session response. Please retry.',
                )
                if (!isSessionInfo(body)) {
                    throw new Error('Server returned an invalid session response. Please retry.')
                }
                return body
            })
            .then((info: SessionInfo) => {
                setSessionInfo(info)
                setState('session-valid')
            })
            .catch(err => {
                setState('error')
                setError(err.message ?? 'Failed to load session')
            })
    }, [sessionId, configId])

    // Once session is valid, look up the connection in the local registry
    useEffect(() => {
        if (state !== 'session-valid') return

        if (!hasIdentity) {
            // The vault probe runs asynchronously in useIdentity(). Do not
            // treat "still checking" as "no vault exists" or the page can
            // flash a false "No identity found" error on first load.
            if (vaultExists === null) {
                return
            }

            if (!vaultExists) {
                setState('error')
                setError('No identity found. Create a profile in SecureBlob first.')
                return
            }

            // Vault exists but locked → need to unlock
            setState('vault-locked')
            return
        }

        // Vault is unlocked → find the connection
        const connections = engine.getMcpConnections()
        const found = connections.find(c => c.configBlobId === configId)
        if (!found) {
            setState('error')
            setError('Connection not found in your account. Make sure you\'re signed in with the correct identity.')
            return
        }

        setConnection(found)
    }, [state, hasIdentity, vaultExists, configId, engine])

    // Handle WebAuthn unlock
    const handleUnlock = useCallback(async () => {
        setState('unlocking')
        try {
            const ok = await unlockWithWebAuthn()
            if (ok) {
                setState('session-valid') // will re-trigger the effect above
            } else {
                setState('vault-locked')
                setError('Authentication cancelled. Try again.')
            }
        } catch (err) {
            setState('vault-locked')
            setError(err instanceof Error ? err.message : 'WebAuthn failed')
        }
    }, [unlockWithWebAuthn])

    // Handle approve
    const handleApprove = useCallback(async () => {
        if (!connection) return

        setState('unlocking')
        try {
            const wsSecret = buildConfigWorkspaceSecret(connection)
            const wsSecretEncoded = encodeWorkspaceSecretV2(wsSecret)
            const approveUrl = `${API_BASE}/mcp/oauth/${configId}/approve`
            const res = await fetch(approveUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session: sessionId,
                    workspaceSecret: wsSecretEncoded,
                }),
            })
            recordBrowserHttpErrorResponse(res, { url: approveUrl, method: 'POST' })

            if (!res.ok) {
                const body = await res.json().catch(() => ({}))
                throw new Error(body.error ?? `Approval failed (${res.status})`)
            }

            const body = await readJsonBody(
                res,
                'Server returned an invalid approval response. Please retry.',
            )
            if (!hasRedirectUrl(body)) {
                throw new Error('Server returned an invalid approval response. Please retry.')
            }
            const { redirectUrl } = body
            setState('approved')

            // Brief "Approved" display, then redirect
            setTimeout(() => {
                setState('redirecting')
                window.location.href = redirectUrl
            }, 800)
        } catch (err) {
            setState('error')
            setError(err instanceof Error ? err.message : 'Approval failed')
        }
    }, [connection, configId, sessionId])

    // Handle deny
    const handleDeny = useCallback(() => {
        if (!sessionInfo) {
            window.close()
            return
        }
        setState('error')
        setError('Access denied. You can close this window.')
    }, [sessionInfo])

    // Get workspace labels for display
    const workspaceLabels = connection
        ? connection.workspaceBlobIds.map(blobId => {
            const ws = engine.getRegistry().find(w => w.blobId === blobId)
            return { blobId, label: ws?.label ?? blobId.slice(0, 8) + '…' }
        })
        : []
    const isCheckingVault = state === 'session-valid' && !hasIdentity && vaultExists === null

    return (
        <div className="mcp-authorize-page">
            <div className="mcp-authorize-card">
                <div className="mcp-authorize-header">
                    <div className="mcp-authorize-logo">
                        <ShieldIcon />
                    </div>
                    <h1 className="mcp-authorize-title">SecureBlob</h1>
                    <p className="mcp-authorize-subtitle">MCP Connection Authorization</p>
                </div>

                {/* Loading */}
                {(state === 'loading' || isCheckingVault) && (
                    <div className="mcp-authorize-status">
                        <div className="mcp-authorize-spinner" />
                        <p className="mcp-authorize-status-text">
                            {state === 'loading' ? 'Verifying session…' : 'Checking local vault…'}
                        </p>
                        <div className="mcp-authorize-shimmer" style={{ width: '60%', margin: '0.5rem auto 0' }} />
                    </div>
                )}

                {/* Error */}
                {state === 'error' && (
                    <div className="mcp-authorize-status">
                        <div className="mcp-authorize-status-icon icon-warning">
                            <AlertIcon />
                        </div>
                        <p className="mcp-authorize-status-text error">{error}</p>
                        {error !== 'Access denied. You can close this window.' && (
                            <div className="mcp-authorize-actions" style={{ marginTop: '0.75rem', width: '100%' }}>
                                <button
                                    className="mcp-authorize-btn mcp-authorize-btn-deny"
                                    onClick={() => window.location.reload()}
                                >
                                    Try again
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Vault locked → WebAuthn prompt */}
                {state === 'vault-locked' && (
                    <>
                        <div className="mcp-authorize-info">
                            <div className="mcp-authorize-info-row">
                                <span className="mcp-authorize-info-label">Client</span>
                                <span className="mcp-authorize-info-value">
                                    {sessionInfo?.redirectDomain ?? 'MCP Client'}
                                </span>
                            </div>
                            <div className="mcp-authorize-info-row">
                                <span className="mcp-authorize-info-label">Config</span>
                                <span className="mcp-authorize-info-value">
                                    {configId.slice(0, 12)}…
                                </span>
                            </div>
                        </div>

                        <p className="mcp-authorize-status-text" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                            Unlock your vault to continue
                        </p>
                        {error && (
                            <p className="mcp-authorize-status-text error" style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.8125rem' }}>
                                {error}
                            </p>
                        )}
                        <div className="mcp-authorize-actions">
                            <button
                                className="mcp-authorize-btn mcp-authorize-btn-primary"
                                onClick={handleUnlock}
                            >
                                <KeyIcon /> Sign in with Passkey
                            </button>
                            <button
                                className="mcp-authorize-btn mcp-authorize-btn-deny"
                                onClick={handleDeny}
                            >
                                Deny
                            </button>
                        </div>
                    </>
                )}

                {/* Unlocking */}
                {state === 'unlocking' && (
                    <div className="mcp-authorize-status">
                        <div className="mcp-authorize-spinner" />
                        <p className="mcp-authorize-status-text">Authenticating…</p>
                    </div>
                )}

                {/* Session valid + identity loaded + connection found → Approve */}
                {(state === 'session-valid' && connection) && (
                    <>
                        <div className="mcp-authorize-info">
                            <div className="mcp-authorize-info-row">
                                <span className="mcp-authorize-info-label">Connection</span>
                                <span className="mcp-authorize-info-value">{connection.label}</span>
                            </div>
                            <div className="mcp-authorize-info-row">
                                <span className="mcp-authorize-info-label">Client</span>
                                <span className="mcp-authorize-info-value">
                                    {sessionInfo?.redirectDomain ?? 'MCP Client'}
                                </span>
                            </div>
                            <div className="mcp-authorize-token-info">
                                <span className="token-icon"><ClockIcon /></span>
                                Token valid for 30 days
                            </div>
                        </div>

                        {/* Scope badges */}
                        <div className="mcp-authorize-scopes">
                            <span className="mcp-authorize-scope-badge">
                                <span className="mcp-authorize-scope-icon">📖</span> Read Files
                            </span>
                            <span className="mcp-authorize-scope-badge">
                                <span className="mcp-authorize-scope-icon">📂</span> List Workspaces
                            </span>
                        </div>

                        {workspaceLabels.length > 0 && (
                            <div className="mcp-authorize-workspaces">
                                <div className="mcp-authorize-workspaces-title">
                                    Workspaces ({workspaceLabels.length})
                                </div>
                                {workspaceLabels.map(ws => (
                                    <div className="mcp-authorize-workspace-item" key={ws.blobId}>
                                        <span className="mcp-authorize-workspace-icon">
                                            <FolderIcon />
                                        </span>
                                        <span className="mcp-authorize-workspace-name">{ws.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="mcp-authorize-actions">
                            <button
                                className="mcp-authorize-btn mcp-authorize-btn-primary"
                                onClick={handleApprove}
                            >
                                Approve & Connect
                            </button>
                            <button
                                className="mcp-authorize-btn mcp-authorize-btn-deny"
                                onClick={handleDeny}
                            >
                                Deny
                            </button>
                        </div>
                    </>
                )}

                {/* Approved */}
                {state === 'approved' && (
                    <div className="mcp-authorize-status">
                        <div className="mcp-authorize-status-icon icon-success">
                            <CheckCircleIcon />
                        </div>
                        <p className="mcp-authorize-status-text success">
                            Approved — redirecting…
                        </p>
                    </div>
                )}

                {/* Redirecting */}
                {state === 'redirecting' && (
                    <div className="mcp-authorize-status">
                        <div className="mcp-authorize-spinner" />
                        <p className="mcp-authorize-status-text">Redirecting to client…</p>
                    </div>
                )}
            </div>

            <p className="mcp-authorize-footer">
                This page was requested by an <strong>MCP client</strong>.
                Only approve if you initiated this connection.
            </p>
        </div>
    )
}
